import { ButtonHTMLAttributes, forwardRef } from "react";
import { ButtonProps, ButtonVariant, ButtonSize } from "./type";
import Spiner from "./loading/Spinner";
import { Link } from "react-router-dom";

const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps
>((props, ref) => {
  const {
    startIcon = undefined,
    endIcon = undefined,
    size = "base",
    variant = "default",
    label,
    onClick,
    type = "button",
    as = undefined,
    disabled = false,
    loading = false,
    children,
    className = "",
    href = "",
    target = "_self",
    ...restProps
  } = props;
  const ring =
    "hover:ring-1 focus:ring-2 active:ring-1 hover:ring-primary focus:ring-primary active:ring-primary";
  // Define class names for each button variant
  const variants: Record<ButtonVariant, string> = {
    default: `text-white bg-blackBase hover:bg-primary focus:bg-primary active:bg-primary ring-1 ring-blackBase ${ring}`,
    contained: `text-white bg-blackBase hover:bg-primary focus:bg-primary active:bg-primary ring-1 ring-blackBase ${ring}`,
    outlined: `text-blackBase bg-white hover:text-primary focus:text-primary active:text-primary ring-1 ring-blackBase ${ring}`,
    transparent: `text-blackBase bg-transparent hover:text-primary focus:text-primary active:text-primary ring-0 ${ring}`,
    glass: `!px-6 !py-4 bg-white/10 backdrop-blur-md border border-white/20 !rounded-full text-white font-medium transition duration-300 hover:bg-white/20 hover:shadow-2xl hover:scale-105 active:scale-95 !text-sm cursor-pointer`,
    primary: `!text-white bg-primary hover:bg-primary focus:bg-primary active:bg-primary ring-1 ring-primary ${ring}`,
    white: `text-blackBase bg-white hover:bg-gray-100 focus:bg-gray-100 active:bg-gray-200 ring-0`,
  };

  const padding = {
    sm: as === "icon" ? "p-1" : "px-2 py-1",
    wide: as === "icon" ? "p-1" : "px-4 py-1",
    base: as === "icon" ? "p-1" : "px-4 py-1",
    md: as === "icon" ? "p-1" : "px-4 py-1",
    lg: as === "icon" ? "p-1" : "px-5 py-2",
  }

  const sizes: Record<ButtonSize, string> = {
    sm: `${padding["sm"]} text-xs gap-1`,
    wide: `${padding["wide"]} text-sm gap-2`,
    base: `${padding["base"]} text-base h-[36px] gap-3`,
    md: `${padding["md"]} text-base gap-3`,
    lg: `${padding["lg"]} text-lg h-[40px] gap-4`,
  };
  const classes = `disabled:cursor-not-allowed disabled:ring-0 disabled:text-graySoft disabled:bg-gray-300 flex flex-row justify-center items-center items-center focus:outline-none font-medium duration-300 rounded-md ${variants[variant]} ${sizes[size]} ${className}`;
  const start = loading ? (
    <Spiner className="!h-5" classSvg="!w-5 !h-5" />
  ) : (
    startIcon
  );
  // @ts-ignore:next-line
  if (as === "link" && href) {
    return (
      <Link
        to={href}
        target={target}
        onClick={onClick}
        className={`${disabled || loading ? "pointer-events-none cursor-not-allowed" : ""
          } ${classes}`}
      >
        {start}
        {label || children}
        {endIcon}
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={classes}
      onClick={onClick}
      ref={ref}
      {...restProps}
    >
      {start}
      {label || children}
      {endIcon}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
